export const FILTERS = [
  { id: "none", name: "Original", favorite: false, settings: {} },
  { id: "clean-pro", name: "Clean Pro", favorite: true, settings: { brightness: 6, contrast: 8, saturation: 4, sharpness: 1 } },
  { id: "portrait-glow", name: "Portrait Glow", favorite: true, settings: { brightness: 9, contrast: -2, saturation: 5, temperature: 8, highlights: -8, shadows: 10, vignette: 8 } },
  { id: "linkedin-studio", name: "LinkedIn Studio", favorite: true, settings: { brightness: 7, contrast: 10, saturation: -5, temperature: 2, sharpness: 1, vignette: 10 } },
  { id: "cinematic-gold", name: "Cinematic Gold", favorite: false, settings: { brightness: -2, contrast: 16, saturation: 10, temperature: 18, shadows: 8, highlights: -14, vignette: 22, grain: 8 } },
  { id: "teal-orange", name: "Teal Orange Film", favorite: true, settings: { contrast: 12, saturation: 18, temperature: 10, tint: -10, shadows: 12, vignette: 18, grain: 7 } },
  { id: "vintage-film", name: "Vintage Film", favorite: false, settings: { brightness: 2, contrast: -8, saturation: -12, temperature: 18, tint: 6, grain: 12, vignette: 18 } },
  { id: "bw-classic", name: "Classic B&W", favorite: false, settings: { contrast: 18, saturation: -100, sharpness: 1, grain: 8, vignette: 14 } },
  { id: "product-white", name: "Product White", favorite: true, settings: { brightness: 14, contrast: 10, saturation: -4, temperature: -4, highlights: 10, shadows: -4, sharpness: 1 } },
  { id: "food-warm", name: "Food Warm", favorite: false, settings: { brightness: 7, contrast: 8, saturation: 16, temperature: 14, highlights: -4 } },
  { id: "travel-vivid", name: "Travel Vivid", favorite: false, settings: { brightness: 5, contrast: 12, saturation: 24, temperature: 2, highlights: -8, shadows: 8 } },
  { id: "night-bright", name: "Night Bright", favorite: false, settings: { brightness: 18, contrast: 8, saturation: 6, shadows: 24, highlights: -18, sharpness: 1 } },
  { id: "document-sharp", name: "Document Sharp", favorite: true, settings: { brightness: 12, contrast: 32, saturation: -55, highlights: 16, shadows: -12, sharpness: 2 } },
  { id: "soft-skin", name: "Soft Skin", favorite: false, settings: { brightness: 8, contrast: -6, saturation: 4, temperature: 8, highlights: -10 } },
  { id: "matte-film", name: "Matte Film", favorite: false, settings: { brightness: 2, contrast: -14, saturation: -6, shadows: 18, highlights: -18, grain: 6 } },
  { id: "high-contrast", name: "High Contrast", favorite: false, settings: { contrast: 34, saturation: 4, shadows: -12, highlights: 10, vignette: 8 } },
  { id: "warm-tone", name: "Warm Tone", favorite: false, settings: { temperature: 20, saturation: 4, brightness: 3 } },
  { id: "cool-tone", name: "Cool Tone", favorite: false, settings: { temperature: -18, tint: -4, contrast: 4 } },
  { id: "custom", name: "Custom", favorite: false, settings: { brightness: 0, contrast: 0, saturation: 0, temperature: 0, tint: 0 } }
];

const clamp = (value, min = -100, max = 100) => Math.min(max, Math.max(min, Number(value) || 0));

export function mergeFilterSettings(filter = {}, overrides = {}, strength = 100) {
  const base = filter.settings || {};
  const ratio = Math.max(0, Math.min(1, strength / 100));
  const merged = { ...base, ...overrides };
  return Object.fromEntries(
    Object.entries(merged).map(([key, value]) => [key, clamp(value) * ratio])
  );
}

export function cssFilterFromSettings(settings = {}) {
  const brightness = 100 + clamp(settings.brightness);
  const contrast = 100 + clamp(settings.contrast);
  const saturation = 100 + clamp(settings.saturation);
  const hue = clamp(settings.tint) * 0.35 + clamp(settings.temperature) * 0.18;
  const blur = Math.max(0, Number(settings.blur) || 0);
  return `brightness(${brightness}%) contrast(${contrast}%) saturate(${saturation}%) hue-rotate(${hue}deg) blur(${blur}px)`;
}

export function getFilterById(id) {
  return FILTERS.find((filter) => filter.id === id) || FILTERS[0];
}

export function applyCanvasOverlays(ctx, width, height, settings = {}) {
  const vignette = Math.max(0, Number(settings.vignette) || 0);
  if (vignette) {
    const gradient = ctx.createRadialGradient(width / 2, height / 2, width * 0.18, width / 2, height / 2, width * 0.72);
    gradient.addColorStop(0, "rgba(0,0,0,0)");
    gradient.addColorStop(1, `rgba(0,0,0,${Math.min(0.55, vignette / 100)})`);
    ctx.fillStyle = gradient;
    ctx.fillRect(0, 0, width, height);
  }

  const grain = Math.max(0, Number(settings.grain) || 0);
  if (grain) {
    const image = ctx.getImageData(0, 0, width, height);
    const amount = grain * 1.8;
    for (let i = 0; i < image.data.length; i += 4) {
      const noise = (Math.random() - 0.5) * amount;
      image.data[i] += noise;
      image.data[i + 1] += noise;
      image.data[i + 2] += noise;
    }
    ctx.putImageData(image, 0, 0);
  }
}

export async function applyFilterToBlob(blob, filter, options = {}) {
  const bitmap = await createImageBitmap(blob);
  const canvas = document.createElement("canvas");
  canvas.width = bitmap.width;
  canvas.height = bitmap.height;
  const ctx = canvas.getContext("2d", { alpha: false });
  const settings = mergeFilterSettings(filter, options.adjustments || {}, options.strength ?? 100);
  ctx.filter = cssFilterFromSettings(settings);
  ctx.translate(options.flipX ? bitmap.width : 0, options.flipY ? bitmap.height : 0);
  ctx.scale(options.flipX ? -1 : 1, options.flipY ? -1 : 1);
  ctx.drawImage(bitmap, 0, 0);
  ctx.setTransform(1, 0, 0, 1, 0, 0);
  ctx.filter = "none";
  applyCanvasOverlays(ctx, canvas.width, canvas.height, settings);
  bitmap.close?.();
  return new Promise((resolve) => canvas.toBlob(resolve, options.type || "image/jpeg", options.quality ?? 0.92));
}
